import { useState, useEffect } from 'react';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import CreateWinnerModal from '../../components/modal/CreateWinnerModal';
import SetDefaultRewardModal from '../../components/modal/SetDefaultRewardModal';

interface GiveawayAttempt {
  id: number;
  user_id: number | null;
  reward_id: string;
  marketor_id: number;
  twitch_id: string;
  refid: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface GiveawayAsset {
  id: number;
  name: string;
  description: string | null;
  price: number;
  category: string | null;
  reward_id: string;
  image_path: string | null;
  status: 'claimed' | 'issuedtomarketors' | 'notclaimed';
}

interface Marketor {
  id: number;
  refid: string;
}

const ManageWinners = () => {
  const [winners, setWinners] = useState<GiveawayAttempt[]>([]);
  const [assets, setAssets] = useState<GiveawayAsset[]>([]);
  const [marketors, setMarketors] = useState<Marketor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingWinnerId, setEditingWinnerId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<Partial<GiveawayAttempt>>({});

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSetDefaultModalOpen, setIsSetDefaultModalOpen] = useState(false);
  const [defaultRewardId, setDefaultRewardId] = useState<string | null>(localStorage.getItem('defaultRewardId'));

  // Asset Counts
  const totalAssets = assets.length;
  const notClaimedCount = assets.filter(a => a.status === 'notclaimed').length;
  const issuedCount = assets.filter(a => a.status === 'issuedtomarketors').length;
  const claimedCount = assets.filter(a => a.status === 'claimed').length;

  const availableAssets = assets.filter(asset => asset.status === 'notclaimed');

  const filteredWinners = winners.filter(winner =>
    winner.twitch_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    winner.refid.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fetchData = async () => {
    try {
      const [winnersRes, assetsRes, marketorsRes] = await Promise.all([
        api.get('/api/all/giveaway_attempts'),
        api.get('/api/all/giveaway_assets'),
        api.get('/api/all/marketors')
      ]);
      setWinners(winnersRes.data);
      setAssets(assetsRes.data);
      setMarketors(marketorsRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSetDefaultReward = (rewardId: string) => {
    setDefaultRewardId(rewardId);
    localStorage.setItem('defaultRewardId', rewardId);
    setIsSetDefaultModalOpen(false);
  };

  const handleCreateWinner = async (newWinnerData: Omit<GiveawayAttempt, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const winnerToCreate = { ...newWinnerData };
      if (!winnerToCreate.reward_id && defaultRewardId) {
        winnerToCreate.reward_id = defaultRewardId;
      } else if (!winnerToCreate.reward_id && availableAssets.length > 0) {
        winnerToCreate.reward_id = availableAssets[0].reward_id; // Fallback to first available
      }

      if (!winnerToCreate.reward_id) {
        console.error("Cannot create winner: No default reward set and no available assets.");
        return;
      }

      await api.post('/api/all/giveaway_attempts', winnerToCreate);
      fetchData();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create winner', error);
    }
  };

  const handleUpdateWinner = async () => {
    if (!editingWinnerId || !editingData) return;
    try {
      await api.patch(`/api/all/giveaway_attempts/${editingWinnerId}`, editingData);
      fetchData();
      setEditingWinnerId(null);
      setEditingData({});
    } catch (error) {
      console.error('Failed to update winner', error);
    }
  };

  const handleDeleteWinner = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this winner?')) {
      try {
        await api.delete(`/api/all/giveaway_attempts/${id}`);
        fetchData();
      } catch (error) {
        console.error('Failed to delete winner', error);
      }
    }
  };

  const startEditing = (winner: GiveawayAttempt) => {
    setEditingWinnerId(winner.id);
    setEditingData({ 
      status: winner.status, 
      reward_id: winner.reward_id,
      twitch_id: winner.twitch_id,
      refid: winner.refid
    });
  };

  const cancelEditing = () => {
    setEditingWinnerId(null);
    setEditingData({});
  };

  const onEditFieldChange = (field: keyof Partial<GiveawayAttempt>, value: string) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-[#00000071]">
      <AdminHeader />
      <div
        style={{
          backgroundImage: "url(/assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex-grow flex flex-col items-center w-full relative pt-6 px-4"
      >
        <div className="w-full max-w-7xl gothic-panel">
          <div className="flex justify-between items-start mb-6">
            <h1 className="gothic-heading">Manage Winners</h1>
            <div className="flex flex-wrap gap-4 justify-end">
                {/* Asset Status Cards */}
                <div className="gothic-summary-card text-center"><h3 className="text-lg font-semibold">Total Assets</h3><p className="text-2xl font-bold">{totalAssets}</p></div>
                <div className="gothic-summary-card text-center"><h3 className="text-lg font-semibold">Not Claimed</h3><p className="text-2xl font-bold">{notClaimedCount}</p></div>
                <div className="gothic-summary-card text-center"><h3 className="text-lg font-semibold">Issued</h3><p className="text-2xl font-bold">{issuedCount}</p></div>
                <div className="gothic-summary-card text-center"><h3 className="text-lg font-semibold">Claimed</h3><p className="text-2xl font-bold">{claimedCount}</p></div>
                
                {/* Controls */}
                <div className="gothic-summary-card text-center">
                    <h3 className="text-lg font-semibold">Default Reward</h3>
                    <p className="text-sm truncate" title={defaultRewardId || 'None'}>{defaultRewardId || 'Not Set'}</p>
                    <button onClick={() => setIsSetDefaultModalOpen(true)} className="gothic-button gothic-button-secondary mt-1">Set Default</button>
                </div>
                <a href="/managegiveaway" className="gothic-button gothic-button-primary flex items-center justify-center text-lg h-full">
                  Add New Asset
                </a>
            </div>
          </div>

          <div className="flex justify-between mb-4">
            <button onClick={() => setIsCreateModalOpen(true)} className="gothic-button gothic-button-primary">
              Add New Winner
            </button>
            <input
              type="text"
              placeholder="Search by Twitch ID or Ref ID..."
              className="gothic-input w-1/3"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Table View */}
          <div className="overflow-x-auto">
            <table className="min-w-full gothic-table">
              <thead>
                <tr>
                  <th>Twitch ID</th>
                  <th>Ref ID</th>
                  <th>Status</th>
                  <th>Assigned Reward</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWinners.map((winner) => (
                  <tr key={winner.id}>
                    <td>
                      {editingWinnerId === winner.id ? (
                        <input
                          type="text"
                          value={editingData.twitch_id}
                          onChange={(e) => onEditFieldChange('twitch_id', e.target.value)}
                          className="gothic-input"
                        />
                      ) : (
                        winner.twitch_id
                      )}
                    </td>
                    <td>
                      {editingWinnerId === winner.id ? (
                        <select
                          value={editingData.refid}
                          onChange={(e) => onEditFieldChange('refid', e.target.value)}
                          className="gothic-input"
                        >
                          {marketors.map(marketor => (
                            <option key={marketor.id} value={marketor.refid}>{marketor.refid}</option>
                          ))}
                        </select>
                      ) : (
                        winner.refid
                      )}
                    </td>
                    <td>
                      {editingWinnerId === winner.id ? (
                        <select
                          value={editingData.status}
                          onChange={(e) => onEditFieldChange('status', e.target.value)}
                          className="gothic-input"
                        >
                          <option value="winner">Winner</option>
                          <option value="claimed">Claimed</option>
                          <option value="pending">Pending</option>
                        </select>
                      ) : (
                        winner.status
                      )}
                    </td>
                    <td>
                      {editingWinnerId === winner.id ? (
                        <select
                          value={editingData.reward_id}
                          onChange={(e) => onEditFieldChange('reward_id', e.target.value)}
                          className="gothic-input"
                        >
                          {winner.reward_id && !availableAssets.find(a => a.reward_id === winner.reward_id) && (
                            <option value={winner.reward_id}>{winner.reward_id} (Claimed/Issued)</option>
                          )}
                          {availableAssets.map(asset => (
                            <option key={asset.id} value={asset.reward_id}>{asset.name} ({asset.reward_id})</option>
                          ))}
                        </select>
                      ) : (
                        winner.reward_id
                      )}
                    </td>
                    <td className="flex items-center space-x-2">
                      {editingWinnerId === winner.id ? (
                        <>
                          <button onClick={handleUpdateWinner} className="gothic-button gothic-button-primary">Save</button>
                          <button onClick={cancelEditing} className="gothic-button gothic-button-secondary">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEditing(winner)} className="gothic-button gothic-button-secondary">Edit</button>
                          <button onClick={() => handleDeleteWinner(winner.id)} className="gothic-button gothic-button-danger">Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isCreateModalOpen && (
        <CreateWinnerModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateWinner}
          availableAssets={availableAssets}
          defaultRewardId={defaultRewardId}
        />
      )}

      {isSetDefaultModalOpen && (
        <SetDefaultRewardModal
            isOpen={isSetDefaultModalOpen}
            onClose={() => setIsSetDefaultModalOpen(false)}
            assets={availableAssets}
            onSetDefault={handleSetDefaultReward}
            currentDefaultId={defaultRewardId}
        />
      )}

      <AdminFooter />
    </div>
  );
};

export default ManageWinners;
